import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, ListGroup, Spinner } from 'react-bootstrap';
import { useUser } from '../components/UserContext.tsx';
import { canViewSafeguardingCases } from './schoolClaims.ts';
import {
  approveCaseUnblind,
  fetchSchoolCase,
  fetchSchoolCases,
  requestCaseUnblind,
  type SchoolSafetyCase
} from './schoolApi.ts';
import SchoolDeskPage from './SchoolDeskPage.tsx';

export default function SchoolCasesPage() {
  const { activeUser } = useUser();
  const [cases, setCases] = useState<SchoolSafetyCase[] | null>(null);
  const [selected, setSelected] = useState<SchoolSafetyCase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const data = await fetchSchoolCases();
    setCases(data.cases || []);
  }, []);

  useEffect(() => {
    if (!canViewSafeguardingCases(activeUser)) return;
    let cancelled = false;
    reload()
      .catch((cause) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Could not load cases');
      });
    return () => {
      cancelled = true;
    };
  }, [activeUser, reload]);

  if (!canViewSafeguardingCases(activeUser)) {
    return (
      <SchoolDeskPage title="Safeguarding cases">
        <Alert variant="secondary">This desk is for the named safeguarding lead and deputy.</Alert>
      </SchoolDeskPage>
    );
  }

  const openCase = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      const data = await fetchSchoolCase(id);
      setSelected(data.case);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not open that case');
    } finally {
      setBusy(false);
    }
  };

  const requestUnblind = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      const data = await requestCaseUnblind(id);
      setSelected(data.case);
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not request unblind');
    } finally {
      setBusy(false);
    }
  };

  const approveUnblind = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      const data = await approveCaseUnblind(id);
      setSelected(data.case);
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not approve unblind');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SchoolDeskPage title="Safeguarding cases">
      <p>
        Relevant words appear only after two named adults unblind a case. Access is time-limited and audited.
        Class teachers do not get this desk.
      </p>
      {error && <Alert variant="danger">{error}</Alert>}
      {cases == null && !error && <Spinner animation="border" />}
      {cases && cases.length === 0 && <p>No laboratory cases yet.</p>}
      {cases && cases.length > 0 && (
        <ListGroup className="mb-3">
          {cases.map((row) => (
            <ListGroup.Item
              key={row.id}
              action
              active={selected?.id === row.id}
              onClick={() => openCase(row.id)}
            >
              {row.severity} · {row.status} · {row.category}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
      {selected && (
        <div>
          <p>
            Unblind requested: {selected.unblind?.requested ? 'yes' : 'no'}
            {selected.unblind?.expiresAt ? ` · expires ${new Date(selected.unblind.expiresAt).toLocaleString()}` : ''}
          </p>
          <Button
            className="me-2"
            variant="outline-primary"
            disabled={busy}
            onClick={() => requestUnblind(selected.id)}
          >
            Request unblind
          </Button>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => approveUnblind(selected.id)}
          >
            Second-adult approve
          </Button>
          {selected.excerpt ? (
            <Alert variant="info" className="mt-3">
              <div><strong>{selected.excerpt.senderShortName}</strong></div>
              <div>{selected.excerpt.text}</div>
            </Alert>
          ) : (
            <p className="mt-3">Excerpt hidden until two adults unblind.</p>
          )}
        </div>
      )}
    </SchoolDeskPage>
  );
}
